import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {AttendanceSummary} from '@/entities/attendance/types';
import {Disclosure} from '@/shared/ui/disclosure';
import {Explain, HelpNote, type Definition} from '@/shared/ui/explain';
import {Icon} from '@/shared/ui/icons';
import {StatBar} from '@/shared/ui/stats';
import {AppText} from '@/shared/ui/text';
import {fonts} from '@/shared/ui/fonts';
import {radii, spacing, useTheme, withAlpha, type Palette} from '@/shared/ui/theme';
import {
  formatCount,
  formatHoursFromMinutes,
  formatHoursTotal,
} from '@/shared/lib/format';
import {formatRate, type RosterBreakdown} from '../lib/aggregate';
import {
  headline,
  observedDays,
  unjudged,
  verdict,
  type VerdictTone,
} from '../lib/narrative';

/**
 * Everything the attendance engine produced for a scope — but read in the order a
 * person actually asks for it.
 *
 * The board used to be twelve equal counters and three percentages, which is complete
 * and unreadable: nothing on it said which number to look at first, and none of the
 * words on it said what they meant. Every visit ended in the same two questions —
 * «ну и как?» and «а как это посчитано?» — and the screen answered neither.
 *
 * So it is now three layers, and each one answers exactly one question:
 *
 *   1. THE SENTENCE  — «ну и как?». One line, in words, painted by how bad it is.
 *      Built in lib/narrative.ts, because the branching (no data / no schedule / clean
 *      / this is what is wrong) is logic, not layout.
 *   2. THREE FIGURES — the numbers worth carrying away, big, each with a hint saying
 *      what it is a share OF. Everything else is a detail of these three.
 *   3. THE COUNTERS  — grouped under headings and explained behind one `?`, instead of
 *      nine equal boxes of jargon. A grouped list is skimmable; a wall is not.
 *
 * Colour still carries meaning and nothing else: a counter at zero stays grey no matter
 * what it counts, so any colour on the board is something to look at — and the note
 * under the grid says so in words rather than leaving it to be inferred.
 *
 * ## Why the rates can be an em dash
 *
 * With no schedule the engine may not call a day late or absent, so `attendanceRate`
 * and `punctualityRate` are null (see lib/aggregate.ts) and both tiles print «—». That
 * is not a gap in the report — it IS the report, and the sentence above spells it out.
 * The previous version printed 100% attendance and 0% punctuality for exactly that
 * case, two invented and mutually contradictory figures on one card.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},

  verdict: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing(3),
    padding: spacing(4),
    borderRadius: radii.xl,
    borderWidth: StyleSheet.hairlineWidth,
  },
  verdictBody: {flex: 1, gap: 3},

  tiles: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  /* The wrapper carries the wrapping, not the tile: `StatTile` sets `flex: 1` on its
     own root, and a `flexBasis` handed to it alongside that is a coin toss in Yoga.
     Three 110pt tiles on a phone is what losing that toss looks like. */
  tile: {flexGrow: 1, flexBasis: 150, minWidth: 150},

  group: {gap: spacing(2), marginTop: spacing(1)},
  groupFirst: {marginTop: 0},
  grid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  counter: {
    flexGrow: 1,
    flexBasis: '30%',
    minWidth: 96,
    borderRadius: radii.md,
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(3),
    gap: 2,
  },
  counterFigure: {fontFamily: fonts.mono.medium, fontSize: 19, lineHeight: 26},
  note: {marginTop: spacing(3)},
});

/** The four verdict tones, as ink on their own wash. */
function verdictSkin(p: Palette, tone: VerdictTone) {
  switch (tone) {
    case 'good':
      return {fg: p.signalInk, mark: p.signal, bg: p.signalSoft, icon: 'check' as const};
    case 'warn':
      return {
        fg: p.warningInk,
        mark: p.warning,
        bg: p.warningSoft,
        icon: 'warning' as const,
      };
    case 'bad':
      return {
        fg: p.destructiveInk,
        mark: p.destructive,
        bg: p.destructiveSoft,
        icon: 'warning' as const,
      };
    case 'neutral':
      return {
        fg: p.foreground,
        mark: p.mutedForeground,
        bg: p.muted,
        icon: 'info' as const,
      };
  }
}

function Counter({
  value,
  label,
  fg,
  bg,
  testID,
}: {
  value: string;
  label: string;
  fg: string;
  bg: string;
  testID?: string;
}) {
  return (
    <View testID={testID} style={[styles.counter, {backgroundColor: bg}]}>
      <AppText style={styles.counterFigure} color={fg}>
        {value}
      </AppText>
      <AppText variant="micro" color={fg} numberOfLines={2}>
        {label}
      </AppText>
    </View>
  );
}

function Group({
  title,
  children,
  first,
}: {
  title: string;
  children: React.ReactNode;
  first?: boolean;
}) {
  return (
    <View style={[styles.group, first && styles.groupFirst]}>
      <AppText variant="eyebrow">{title}</AppText>
      <View style={styles.grid}>{children}</View>
    </View>
  );
}

export function SummaryBoard({
  summary,
  /**
   * Разложение выборки по людям — есть только там, где отчёт о нескольких. Отчёт по
   * одному сотруднику его не передаёт, и тогда ни строки «ни разу не отметились», ни
   * ветки вердикта про молчащий список на экране не появляется: для одного человека
   * «ни одной отметки» — это сам ответ, а не объяснение к нему.
   */
  roster,
}: {
  summary: AttendanceSummary;
  roster?: RosterBreakdown;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  // Closed on arrival. The three tiles above are what a visit is usually for; the nine
  // counters are the audit trail behind them, and a board that opens with all twelve
  // is the wall this component was rebuilt to stop being.
  const [detail, setDetail] = useState(false);

  const hour = t('attendance.units.hour');
  const minute = t('attendance.units.minute');

  const h = headline(summary);
  const attendance = formatRate(h.attendance);
  const v = verdict(summary, attendance, roster);
  const skin = verdictSkin(p, v.tone);
  const judged = !unjudged(summary);

  // Часами, без минут: см. formatHoursTotal. Среднее за день ниже — по-прежнему с минутами.
  const worked = formatHoursTotal(summary.total_worked_minutes, hour, minute);

  const quiet = {fg: p.mutedForeground, bg: p.muted};
  const warn = {fg: p.warningInk, bg: p.warningSoft};
  const bad = {fg: p.destructiveInk, bg: p.destructiveSoft};
  // "On time" is a verdict, not an accent — green, the same green the access feed
  // paints a granted pass with.
  const good = {fg: p.signalInk, bg: p.signalSoft};
  const plain = {fg: p.foreground, bg: p.muted};

  const tone = (value: number, hot: typeof warn) => (value > 0 ? hot : quiet);

  /**
   * What the closed section already answers. Only the counters that FIRED are named —
   * a summary line reading «Пропусков 0 · Опозданий 0» is a line that has to be read to
   * learn nothing, and the whole point of stating a closed section's contents is that
   * most visits never open it.
   */
  const fired = [
    [summary.absent_days, t('attendance.summary.absent')],
    [summary.late_days, t('attendance.summary.late')],
    [summary.early_leave_days, t('attendance.flag.earlyLeave')],
    [summary.overtime_days, t('attendance.summary.overtime')],
    [summary.incomplete_days, t('attendance.flag.incomplete')],
    [summary.unscheduled_days, t('attendance.flag.unscheduled')],
  ] as const;
  const named = fired
    .filter(([value]) => (value as number) > 0)
    .map(([value, label]) => `${label} ${formatCount(value as number)}`);
  const countersDigest =
    named.length > 0 ? named.join('  ·  ') : t('reports.countersNothing');

  /** The glossary behind the `?`, in the order the counters appear below it. */
  const definitions: Definition[] = [
    'attendance',
    'punctuality',
    'worked',
    'averageDay',
    'present',
    'absent',
    'unscheduled',
    'onTime',
    'late',
    'earlyArrival',
    'earlyLeave',
    'overtime',
    'incomplete',
  ].map(id => ({
    term: t(`reports.def.${id}.term`),
    meaning: t(`reports.def.${id}.meaning`),
    formula: t(`reports.def.${id}.formula`, {defaultValue: ''}) || undefined,
  }));

  return (
    <View style={styles.root}>
      {/* 1 ── the sentence. */}
      <View
        testID="report-verdict"
        style={[
          styles.verdict,
          {backgroundColor: skin.bg, borderColor: withAlpha(skin.mark, 0.3)},
        ]}>
        <Icon name={skin.icon} size={20} color={skin.mark} />
        {/*
          One sentence, and nothing under it.
          
          There used to be a meta line here — period · headcount · scheduled days — and
          every part of it was already on screen within a hundred points: the period in
          the toolbar pill above, the headcount in the page header, and the scheduled
          days as the denominator printed under the attendance tile below («197 из 216 по
          графику»). Three restatements of things the reader had just read, in the one
          block that exists to say the thing nothing else says.
        */}
        <View style={styles.verdictBody}>
          <AppText variant="subtitle" color={skin.fg}>
            {t(v.key, v.params)}
          </AppText>
        </View>
      </View>

      {/* 2 ── три числа, которые уносят с собой.
          Раньше это были три КАРТОЧКИ со значком, рамкой, тенью и полосой-индикатором
          под каждой: на телефоне они занимали два экрана прокрутки, чтобы сообщить три
          числа, и до «с кем разбираться» приходилось долистывать. Полоса говорит ровно
          то же самое — доля названа словами в подписи под цифрой («20 из 22 по
          графику»), а это и есть то, что рисовал индикатор. */}
      <StatBar
        columns={3}
        items={[
          {
            testID: 'summary-attendance',
            label: t('reports.rate.attendance'),
            value: attendance,
            // Подпись — это знаменатель, названный вслух. Процент, у которого не
            // сказано целое, — это то число, о котором потом спорят.
            hint: judged
              ? t('reports.ofScheduled', {
                  present: formatCount(h.presentDays),
                  expected: formatCount(h.expected),
                })
              : t('reports.noScheduleShort'),
            tone: h.attendance === 1 ? p.signalInk : undefined,
          },
          {
            testID: 'summary-punctuality',
            label: t('reports.rate.punctuality'),
            value: formatRate(h.punctuality),
            hint:
              judged && h.presentDays > 0
                ? t('reports.ofPresent', {
                    onTime: formatCount(h.punctualDays),
                    present: formatCount(h.presentDays),
                  })
                : t('reports.noScheduleShort'),
            tone: h.punctuality === 1 ? p.signalInk : undefined,
          },
          {
            testID: 'summary-worked',
            label: t('attendance.summary.worked'),
            value: worked,
            hint:
              h.averageMinutes == null
                ? null
                : t('reports.perDay', {
                    value: formatHoursFromMinutes(h.averageMinutes, hour, minute),
                  }),
          },
          /*
            Четвёртое число появляется только тогда, когда ему есть что сказать.

            Это единственная цифра на доске, считающая ЛЮДЕЙ, а не дни, и стоит она
            здесь потому, что объясняет первую: пятьсот человек, не отметившихся ни
            разу, отдали в пропуски все свои плановые дни и утянули общую явку туда,
            где по ней уже ничего не решишь. Без этой строки читатель видит «20%» и
            делает вывод о дисциплине — а вывод надо делать о том, все ли проходные
            оборудованы и весь ли список актуален.

            Жёлтым, а не красным: это вопрос к данным, а не приговор людям.
          */
          ...(roster && roster.silent > 0
            ? [
                {
                  testID: 'summary-silent',
                  label: t('reports.roster.silent'),
                  value: formatCount(roster.silent),
                  hint: t('reports.roster.silentHint', {
                    total: formatCount(roster.total),
                  }),
                  tone: p.warningInk,
                },
              ]
            : []),
        ]}
      />

      {roster && roster.silent > 0 ? (
        <HelpNote testID="silent-explain" text={t('reports.roster.silentExplain')} />
      ) : null}

      {/* 3 ── the counters, grouped and explained, behind one disclosure. */}
      <Disclosure
        testID="counters"
        title={t('reports.counters')}
        summary={countersDigest}
        open={detail}
        onToggle={() => setDetail(open => !open)}
        aside={
          <Explain
            testID="explain-counters"
            title={t('reports.glossary')}
            subtitle={t('reports.glossaryHint')}
            definitions={definitions}
            note={t('reports.glossaryNote')}
          />
        }>
        <Group title={t('reports.group.days')} first>
          <Counter
            value={`${formatCount(summary.present_days)}/${formatCount(
              // A scope with no schedules reports zero scheduled days and puts
              // everything in `unscheduled` — showing "x/0" would read as broken, so the
              // denominator falls back to what was actually observed.
              observedDays(summary),
            )}`}
            label={t('attendance.summary.present')}
            testID="counter-present"
            {...plain}
          />
          <Counter
            value={formatCount(summary.absent_days)}
            label={t('attendance.summary.absent')}
            testID="counter-absent"
            {...tone(summary.absent_days, bad)}
          />
          <Counter
            value={formatCount(summary.unscheduled_days)}
            label={t('attendance.flag.unscheduled')}
            testID="counter-unscheduled"
            {...quiet}
          />
        </Group>

        <Group title={t('reports.group.clock')}>
          <Counter
            value={formatCount(summary.on_time_days)}
            label={t('attendance.flag.onTime')}
            testID="counter-on-time"
            {...(summary.on_time_days > 0 ? good : quiet)}
          />
          <Counter
            value={formatCount(summary.late_days)}
            label={t('attendance.summary.late')}
            testID="counter-late"
            {...tone(summary.late_days, warn)}
          />
          <Counter
            value={formatCount(summary.early_arrival_days)}
            label={t('attendance.flag.earlyArrival')}
            testID="counter-early-arrival"
            {...quiet}
          />
          <Counter
            value={formatCount(summary.early_leave_days)}
            label={t('attendance.flag.earlyLeave')}
            testID="counter-early-leave"
            {...tone(summary.early_leave_days, warn)}
          />
        </Group>

        <Group title={t('reports.group.special')}>
          <Counter
            value={formatCount(summary.overtime_days)}
            label={t('attendance.summary.overtime')}
            testID="counter-overtime"
            {...tone(summary.overtime_days, warn)}
          />
          <Counter
            value={formatCount(summary.incomplete_days)}
            label={t('attendance.flag.incomplete')}
            testID="counter-incomplete"
            {...tone(summary.incomplete_days, warn)}
          />
        </Group>

        <HelpNote style={styles.note} text={t('reports.zeroIsGrey')} />
      </Disclosure>
    </View>
  );
}
